import { api, Booking } from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import { promisifyApi } from "@/utility/utils";
import {
  GridLegacy as Grid,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Typography,
} from "@mui/material";

import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../styles";
import { BookingFabLabel } from "../BookingFab/BookingFabV2";
import { FiSearch } from "react-icons/fi";
import BarcodeScanner from "@/components/thirdParty/react-barcode-reader";
import useSound from "use-sound";
import wrongKeySfx from "../../../assets/wrong-key.wav";
import correctKeySfx from "../../../assets/correct-key.wav";
import busySfx from "../../../assets/busy.mp3";
import warningSfx from "../../../assets/warning.mp3";

export const BookingFindKey: React.FunctionComponent<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <Fab
      style={{ ...styles.fab, backgroundColor: "rgba(100, 100, 100, 1)" }}
      onClick={async () => {
        onClick();
      }}
      data-testid="Fab-Find-Key"
    >
      <BookingFabLabel text={t("booking.labels.findKey")}>
        <FiSearch size={26} />
      </BookingFabLabel>
    </Fab>
  );
};

enum FindKeyResult {
  none = "none",
  Busy = "busy",
  CorrectKey = "correct",
  WrongKey = "wrong",
}

const resultColors: Record<FindKeyResult, string> = {
  [FindKeyResult.none]: "#cccccc",
  [FindKeyResult.Busy]: "#f5a623",
  [FindKeyResult.CorrectKey]: "#4caf50",
  [FindKeyResult.WrongKey]: "#f44336",
};

const resultLabels: Record<FindKeyResult, string> = {
  [FindKeyResult.none]: "booking.findKey.waitingForScan",
  [FindKeyResult.Busy]: "booking.findKey.findKeyBusy",
  [FindKeyResult.CorrectKey]: "booking.findKey.keyFound",
  [FindKeyResult.WrongKey]: "booking.findKey.keyNotFound",
};

export const FindKeyDialog: React.FC<{
  booking: Booking;
  show: boolean;
  onHide: () => void;
}> = ({ booking, show, onHide }) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const getBookingByScan = promisifyApi(api.hooks.booking.getByScan());
  const [correctKey] = useSound(correctKeySfx);
  const [wrongKey] = useSound(wrongKeySfx);
  const [busySound] = useSound(busySfx);
  const [warningSound] = useSound(warningSfx);

  const [bookingFound, setBookingFound] = useState<Booking | null>(null);
  const [result, setResult] = useState<FindKeyResult>(FindKeyResult.none);

  useEffect(() => {
    if (show) {
      setResult(FindKeyResult.none);
      setBookingFound(null);
    }
  }, [show]);

  const onBarcodeScanned = async (scan: string) => {
    if (result === FindKeyResult.CorrectKey) {
      // Prevent further scanning once the correct key is identified.
      return;
    }
    if (result === FindKeyResult.Busy) {
      busySound();
      return;
    }

    setResult(FindKeyResult.Busy);

    try {
      const res = await getBookingByScan.send(session.current.context, { scan });
      if (res?.id !== booking.id) {
        wrongKey();
        setResult(FindKeyResult.WrongKey);
      } else {
        correctKey();
        setResult(FindKeyResult.CorrectKey);
        setBookingFound(res);
      }
    } catch (err) {
      warningSound();
      setResult(FindKeyResult.WrongKey);
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      fullWidth
      maxWidth="sm"
      data-testid="FindKeyDialog"
    >
      <DialogTitle data-testid="FindKeyDialog-Title">
        {t("booking.findKey.title")}
      </DialogTitle>

      <DialogContent data-testid="FindKeyDialog-Content">
        {result !== FindKeyResult.CorrectKey && (
          <Typography data-testid="FindKeyDialog-Instructions">
            {t("booking.findKey.scanInstructions")}
          </Typography>
        )}

        <Box
          mt={3}
          mb={3}
          p={2}
          borderRadius={2}
          bgcolor={resultColors[result]}
          textAlign="center"
          data-testid="FindKeyDialog-Result"
        >
          <Typography variant="h6" color="white">
            {t(resultLabels[result])}
          </Typography>
        </Box>

        {result === FindKeyResult.CorrectKey && bookingFound && (
          <FindKeyBookingDetails booking={bookingFound} />
        )}

        {show && result !== FindKeyResult.CorrectKey && (
          <BarcodeScanner
            onError={(err: string) => console.log(err)}
            onScan={(scan: string) => onBarcodeScanned(scan)}
            minLength={1}
            data-testid="FindKeyDialog-Scanner"
          />
        )}
      </DialogContent>

      <DialogActions data-testid="FindKeyDialog-Actions">
        <Button onClick={onHide} color="primary" data-testid="FindKeyDialog-Close">
          {t("booking.findKey.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Value = ({ children }: { children: React.ReactNode }) => (
  <Grid item xs={8} style={{ fontWeight: 600, fontSize: "1.2rem" }}>
    {children}
  </Grid>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <Grid item xs={4} style={{ textAlign: "right" }}>
    <div style={{ marginRight: 8 }}>{children}:</div>
  </Grid>
);

const FindKeyBookingDetails: React.FunctionComponent<{ booking: Booking }> = ({
  booking,
}) => {
  const { t } = useTranslation();
  return (
    <Box mt={3} data-testid="FindKeyDialog-BookingDetails">
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t("booking.labels.bookingDetails")}
          </Typography>

          <Grid container spacing={1}>
            <Label>{t("booking.labels.bookingRef")}</Label>
            <Value>{booking.ref}</Value>

            <Label>{t("booking.labels.registration")}</Label>
            <Value>{booking.vehicle.registration}</Value>

            <Label>{t("booking.labels.make")}</Label>
            <Value>{booking.vehicle.make}</Value>

            <Label>{t("booking.labels.model")}</Label>
            <Value>{booking.vehicle.model}</Value>

            <Label>{t("booking.labels.colour")}</Label>
            <Value>{booking.vehicle.colour}</Value>
          </Grid>

          <Box mt={2}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("booking.labels.vehicleLocation")}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4} />
              <Value>
                {[booking.vehicle.location.name, booking.vehicle.location.parentName]
                  .filter(Boolean)
                  .join(", ")}
              </Value>
            </Grid>
          </Box>

          <Box mt={2}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("booking.labels.keyLocation")}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4} />
              <Value>
                {[booking.vehicleKey.location?.name, booking.vehicleKey.location?.parentName]
                  .filter(Boolean)
                  .join(", ")}
              </Value>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
